import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  Search,
  Book,
  HelpCircle,
  MessageSquare,
  ChevronRight,
  ArrowLeft,
  Sparkles,
  Rocket,
  BookOpen,
  DollarSign,
  Paintbrush,
  Wrench,
  Users,
  Building,
  Scale,
  Phone,
  Plus,
  ThumbsUp,
  Eye,
  Send,
  Loader2,
  FileText
} from "lucide-react";

const CATEGORY_ICONS = {
  getting_started: <Rocket className="w-5 h-5" />,
  rules_regulations: <BookOpen className="w-5 h-5" />,
  design_review: <Paintbrush className="w-5 h-5" />,
  payments_fees: <DollarSign className="w-5 h-5" />,
  maintenance: <Wrench className="w-5 h-5" />,
  community: <Users className="w-5 h-5" />,
  metro_district: <Building className="w-5 h-5" />,
  legal: <Scale className="w-5 h-5" />,
  contact: <Phone className="w-5 h-5" />
};

const KnowledgeBasePage = ({ isAdmin = false }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [activeTab, setActiveTab] = useState("browse");
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Article detail
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleOpen, setArticleOpen] = useState(false);
  
  // AI Help
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  
  // Admin: Create/Edit
  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState("article");
  const [newContent, setNewContent] = useState({
    title: "",
    question: "",
    answer: "",
    category: "getting_started",
    content: "",
    tags: "",
    is_featured: false
  });

  useEffect(() => {
    fetchCategories();
    fetchContent();
  }, [selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/knowledge-base/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const fetchContent = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append("category", selectedCategory);
      if (searchQuery) params.append("search", searchQuery);
      
      const [articlesRes, faqsRes] = await Promise.all([
        axios.get(`${API}/knowledge-base/articles?${params.toString()}`),
        axios.get(`${API}/knowledge-base/faqs?${params.toString()}`)
      ]);
      
      setArticles(articlesRes.data.articles);
      setFaqs(faqsRes.data.faqs);
    } catch (error) {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const handleViewArticle = async (article) => {
    try {
      const response = await axios.get(`${API}/knowledge-base/articles/${article.article_id}`);
      setSelectedArticle(response.data);
      setArticleOpen(true);
    } catch (error) {
      toast.error("Failed to load article");
    }
  };

  const handleMarkHelpful = async (articleId) => {
    try {
      await axios.post(`${API}/knowledge-base/articles/${articleId}/helpful?helpful=true`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Thank you for your feedback!");
      if (selectedArticle?.article_id === articleId) {
        setSelectedArticle({ ...selectedArticle, helpful_count: (selectedArticle.helpful_count || 0) + 1 });
      }
    } catch (error) {
      toast.error("Failed to submit feedback");
    }
  };

  const handleAskQuestion = async () => {
    if (!aiQuestion.trim()) {
      toast.error("Please enter a question");
      return;
    }
    
    setAiLoading(true);
    setAiAnswer(null);
    
    try {
      const response = await axios.post(`${API}/knowledge-base/ai/ask`, null, {
        params: { question: aiQuestion },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAiAnswer(response.data);
    } catch (error) {
      toast.error("Failed to get answer");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSearchSuggestions = async (query) => {
    setAiQuestion(query);
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/knowledge-base/ai/suggestions`, {
        params: { partial_query: query }
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Suggestions error:", error);
    }
  };

  const handleCreate = async () => {
    try {
      const tags = newContent.tags.split(",").map(t => t.trim()).filter(t => t);
      
      if (createType === "article") {
        await axios.post(`${API}/knowledge-base/articles`, {
          title: newContent.title,
          category: newContent.category,
          content: newContent.content,
          tags,
          is_featured: newContent.is_featured
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Article created successfully");
      } else {
        await axios.post(`${API}/knowledge-base/faqs`, {
          question: newContent.question,
          answer: newContent.answer,
          category: newContent.category,
          tags
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("FAQ created successfully");
      }
      
      setCreateOpen(false);
      setNewContent({ title: "", question: "", answer: "", category: "getting_started", content: "", tags: "", is_featured: false });
      fetchContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create content");
    }
  };

  const handleSeedContent = async () => {
    try {
      const response = await axios.post(`${API}/knowledge-base/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchContent();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to seed content");
    }
  };

  const isBoard = ["admin", "board", "property_manager", "drc"].includes(user?.role);

  const content = (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Book className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-serif text-lg font-semibold block">
                  Help Center
                </span>
                <span className="text-xs text-muted-foreground">
                  Knowledge Base & AI Assistance
                </span>
              </div>
            </div>
          </div>
          
          {isAdmin && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleSeedContent}>
                Seed Default Content
              </Button>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Content
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Hero Search */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2">How can we help you?</h1>
          <p className="text-muted-foreground mb-6">Search our knowledge base or ask our AI assistant</p>
          
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              value={aiQuestion}
              onChange={(e) => handleSearchSuggestions(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAskQuestion()}
              placeholder="Search articles or ask a question..."
              className="pl-12 pr-24 py-6 text-lg"
              data-testid="search-input"
            />
            <Button 
              className="absolute right-2 top-1/2 -translate-y-1/2"
              onClick={handleAskQuestion}
              disabled={aiLoading}
              data-testid="ask-btn"
            >
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
            
            {/* Search Suggestions */}
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border rounded-lg shadow-lg z-50">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    className="w-full px-4 py-2 text-left hover:bg-muted/50 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
                    onClick={() => {
                      if (s.type === "faq") {
                        setAiQuestion(s.text);
                        handleAskQuestion();
                      } else {
                        const article = articles.find(a => a.article_id === s.id);
                        if (article) handleViewArticle(article);
                      }
                      setSuggestions([]);
                    }}
                  >
                    {s.type === "faq" ? <HelpCircle className="w-4 h-4 text-blue-500" /> : <FileText className="w-4 h-4 text-green-500" />}
                    <span className="text-sm">{s.text}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* AI Answer */}
        {aiAnswer && (
          <Card className="mb-8 border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Sparkles className="w-5 h-5 text-primary" />
                AI Answer
                {!aiAnswer.ai_generated && <Badge variant="outline">Template Response</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap">{aiAnswer.answer}</p>
              </div>
              
              {(aiAnswer.related_articles?.length > 0 || aiAnswer.related_faqs?.length > 0) && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium mb-2">Related Resources:</p>
                  <div className="flex flex-wrap gap-2">
                    {aiAnswer.related_articles?.map((a, i) => (
                      <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => handleViewArticle(a)}>
                        <FileText className="w-3 h-3 mr-1" />
                        {a.title}
                      </Badge>
                    ))}
                    {aiAnswer.related_faqs?.map((f, i) => (
                      <Badge key={i} variant="outline">
                        <HelpCircle className="w-3 h-3 mr-1" />
                        {f.question?.substring(0, 30)}...
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-3">
            <TabsTrigger value="browse" data-testid="tab-browse">
              <Book className="w-4 h-4 mr-2" />
              Browse
            </TabsTrigger>
            <TabsTrigger value="faqs" data-testid="tab-faqs">
              <HelpCircle className="w-4 h-4 mr-2" />
              FAQs
            </TabsTrigger>
            <TabsTrigger value="ask" data-testid="tab-ask">
              <MessageSquare className="w-4 h-4 mr-2" />
              Ask AI
            </TabsTrigger>
          </TabsList>

        {/* Category Filter */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={selectedCategory === "" ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory("")}
          >
            All
          </Button>
          {categories.map((cat) => (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat.value)}
              className="flex items-center gap-2"
            >
              {CATEGORY_ICONS[cat.value]}
              {cat.label}
              <Badge variant="secondary" className="ml-1">{cat.total_count}</Badge>
            </Button>
          ))}
        </div>

        {/* Content */}
        <TabsContent value="browse" className="mt-0">
          {/* Featured Articles */}
          {articles.filter(a => a.is_featured).length > 0 && !selectedCategory && (
            <div className="mb-8">
              <h2 className="font-serif text-xl font-semibold mb-4">Featured Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {articles.filter(a => a.is_featured).map((article) => (
                  <Card 
                    key={article.article_id} 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleViewArticle(article)}
                    data-testid={`article-${article.article_id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                        {CATEGORY_ICONS[article.category]}
                        {categories.find(c => c.value === article.category)?.label}
                      </div>
                      <CardTitle className="text-lg">{article.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2">{article.summary}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{article.view_count || 0}</span>
                        <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{article.helpful_count || 0}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* All Articles */}
          <h2 className="font-serif text-xl font-semibold mb-4">
            {selectedCategory ? categories.find(c => c.value === selectedCategory)?.label : "All Articles"}
          </h2>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Book className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No articles found</p>
              {isAdmin && <p className="text-sm mt-2">Click &ldquo;Seed Default Content&rdquo; to add starter articles</p>}
            </div>
          ) : (
            <div className="space-y-3">
              {articles.map((article) => (
                <Card 
                  key={article.article_id}
                  className="cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => handleViewArticle(article)}
                >
                  <CardContent className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        {CATEGORY_ICONS[article.category]}
                      </div>
                      <div>
                        <h3 className="font-medium">{article.title}</h3>
                        <p className="text-sm text-muted-foreground">{article.summary?.substring(0, 80)}...</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="faqs" className="mt-0">
          <h2 className="font-serif text-xl font-semibold mb-4">Frequently Asked Questions</h2>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : faqs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No FAQs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {faqs.map((faq) => (
                <Card key={faq.faq_id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-start gap-2">
                      <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      {faq.question}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground pl-7">{faq.answer}</p>
                    {faq.tags?.length > 0 && (
                      <div className="flex gap-1 mt-2 pl-7">
                        {faq.tags.map((tag, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="ask" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Ask Our AI Assistant
              </CardTitle>
              <CardDescription>
                Get instant answers to your HOA questions. Our AI is trained on community documents and policies.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Your Question</Label>
                  <Textarea
                    value={aiQuestion}
                    onChange={(e) => setAiQuestion(e.target.value)}
                    placeholder="e.g., What are the fence height restrictions? How do I submit a DRC application?"
                    rows={4}
                    data-testid="ai-question-input"
                  />
                </div>
                <Button onClick={handleAskQuestion} disabled={aiLoading} className="w-full">
                  {aiLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Getting Answer...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Ask Question
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        </Tabs>
      </main>

      {/* Article Detail Dialog */}
      <Dialog open={articleOpen} onOpenChange={setArticleOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedArticle && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  {CATEGORY_ICONS[selectedArticle.category]}
                  {categories.find(c => c.value === selectedArticle.category)?.label}
                </div>
                <DialogTitle className="font-serif text-2xl">{selectedArticle.title}</DialogTitle>
              </DialogHeader>
              
              <div className="prose prose-sm max-w-none py-4">
                <div dangerouslySetInnerHTML={{ __html: selectedArticle.content.replace(/\n/g, '<br/>').replace(/^# (.*$)/gm, '<h1>$1</h1>').replace(/^## (.*$)/gm, '<h2>$1</h2>').replace(/^### (.*$)/gm, '<h3>$1</h3>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/- (.*$)/gm, '<li>$1</li>') }} />
              </div>
              
              {selectedArticle.tags?.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {selectedArticle.tags.map((tag, i) => (
                    <Badge key={i} variant="outline">{tag}</Badge>
                  ))}
                </div>
              )}
              
              <DialogFooter className="flex-col sm:flex-row gap-2 border-t pt-4">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{selectedArticle.view_count || 0} views</span>
                  <span className="flex items-center gap-1"><ThumbsUp className="w-4 h-4" />{selectedArticle.helpful_count || 0} found helpful</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleMarkHelpful(selectedArticle.article_id)}>
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Helpful
                  </Button>
                  <Button onClick={() => setArticleOpen(false)}>Close</Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Content Dialog (Admin) */}
      {isAdmin && (
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Knowledge Base Content</DialogTitle>
            </DialogHeader>
            
            <Tabs value={createType} onValueChange={setCreateType}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="article">Article</TabsTrigger>
                <TabsTrigger value="faq">FAQ</TabsTrigger>
              </TabsList>
              
              <TabsContent value="article" className="space-y-4 mt-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={newContent.title}
                    onChange={(e) => setNewContent({ ...newContent, title: e.target.value })}
                    placeholder="Article title"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={newContent.category} onValueChange={(v) => setNewContent({ ...newContent, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Content (Markdown supported)</Label>
                  <Textarea
                    value={newContent.content}
                    onChange={(e) => setNewContent({ ...newContent, content: e.target.value })}
                    placeholder="# Article Title\n\nYour content here..."
                    rows={10}
                  />
                </div>
                <div>
                  <Label>Tags (comma-separated)</Label>
                  <Input
                    value={newContent.tags}
                    onChange={(e) => setNewContent({ ...newContent, tags: e.target.value })}
                    placeholder="dues, payment, fees"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="faq" className="space-y-4 mt-4">
                <div>
                  <Label>Question</Label>
                  <Input
                    value={newContent.question}
                    onChange={(e) => setNewContent({ ...newContent, question: e.target.value })}
                    placeholder="What is the question?"
                  />
                </div>
                <div>
                  <Label>Answer</Label>
                  <Textarea
                    value={newContent.answer}
                    onChange={(e) => setNewContent({ ...newContent, answer: e.target.value })}
                    placeholder="Provide the answer..."
                    rows={5}
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={newContent.category} onValueChange={(v) => setNewContent({ ...newContent, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tags (comma-separated)</Label>
                  <Input
                    value={newContent.tags}
                    onChange={(e) => setNewContent({ ...newContent, tags: e.target.value })}
                    placeholder="dues, payment, fees"
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );

  return isBoard ? <AdminSidebar>{content}</AdminSidebar> : content;
};

export default KnowledgeBasePage;
